import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Toggle } from '@kognitos/lattice';
import {
  getCurrentDate,
  isInFirstOrLastWeekOfMonth,
} from '@/shared/utils/datetime-utils';
import type { TSchedulePreset } from './types';
import { computePresetLabelValues, getPresetOptionRows } from './utils';

/**
 * Props for the SchedulePresets component.
 */
export interface ISchedulePresetsProps {
  /** Currently selected preset */
  selectedPreset?: TSchedulePreset;
  /** Callback when a preset is selected or deselected */
  onPresetSelect?: (preset: TSchedulePreset | undefined) => void;
  /** Date used to dynamically compute preset labels. Defaults to current date if not provided */
  referenceDate?: Date;
  /** Whether the preset toggles are disabled */
  disabled?: boolean;
}

/**
 * A component for displaying and selecting schedule preset options.
 *
 * @param props - Component props
 * @returns JSX element containing the schedule preset options
 */
export function SchedulePresets({
  selectedPreset,
  onPresetSelect,
  referenceDate,
  disabled = false,
}: ISchedulePresetsProps) {
  const { t } = useTranslation('automation');

  // Compute the date to use for preset calculations
  const dateToUse = useMemo(
    () => referenceDate ?? getCurrentDate(),
    [referenceDate],
  );

  // Memoize preset options to avoid recalculating on every render
  const presetRows = useMemo(() => {
    const showAllPresets = isInFirstOrLastWeekOfMonth(dateToUse);
    return getPresetOptionRows(showAllPresets);
  }, [dateToUse]);

  // Compute label values for dynamic preset labels (only re-computes when date changes)
  const presetLabelValues = useMemo(
    () => computePresetLabelValues(dateToUse),
    [dateToUse],
  );

  return (
    <Flex
      direction="column"
      gap={2}
      width="full"
      role="group"
      aria-label={t('automation.schedule.repeats.presets')}
      data-testid="schedule-presets"
    >
      {presetRows.map(row => (
        <Flex key={row.map(p => p.value).join('-')} gap={2} width="full">
          {row.map(preset => (
            <Toggle
              key={preset.value}
              pressed={selectedPreset === preset.value}
              onPressedChange={pressed =>
                onPresetSelect?.(pressed ? preset.value : undefined)
              }
              variant="outline"
              className="h-9 flex-1 bg-background cursor-pointer"
              aria-label={t(preset.labelKey, presetLabelValues)}
              data-testid={`schedule-preset-${preset.value}`}
              disabled={disabled}
            >
              {t(preset.labelKey, presetLabelValues)}
            </Toggle>
          ))}
        </Flex>
      ))}
    </Flex>
  );
}
